import {PatientTypes} from '@shared/constants/Mapping';

const UPDATED_AFTER_CREATE_MS = 2000;

export function isImportedPatientRecord(
  row?: {
    patient_type?: string | null;
  } | null
): boolean {
  return (
    row?.patient_type === PatientTypes.IMPORTED || row?.patient_type == null
  );
}

export function isRecordUpdatedByTimestamp(
  createdAt?: string | Date | null,
  modifiedAt?: string | Date | null
): boolean {
  if (!createdAt || !modifiedAt) {
    return false;
  }
  return (
    new Date(modifiedAt).getTime() - new Date(createdAt).getTime() >
    UPDATED_AFTER_CREATE_MS
  );
}

export function shouldShowPatientEmrSyncActions(
  row?: {
    patient_type?: string | null;
    created_at?: string | Date | null;
    modified_at?: string | Date | null;
    record_status?: string | null;
    guarantor_created_at?: string | Date | null;
    guarantor_modified_at?: string | Date | null;
    guarantor_record_status?: string | null;
  } | null
): boolean {
  if (!isImportedPatientRecord(row)) {
    return true;
  }
  return (
    (isRecordUpdatedByTimestamp(row?.created_at, row?.modified_at) &&
      row?.record_status === 'updated') ||
    (isRecordUpdatedByTimestamp(
      row?.guarantor_created_at,
      row?.guarantor_modified_at
    ) &&
      row?.guarantor_record_status === 'updated')
  );
}

export function isPatientEmrUpdateConfirmed(row: {
  posted_to_emr?: boolean;
  updated_in_emr?: boolean;
  updated_in_emr_by?: string | number | null;
  updated_in_emr_at?: string | null;
}): boolean {
  return Boolean(
    row?.posted_to_emr &&
      row?.updated_in_emr &&
      row?.updated_in_emr_by != null &&
      String(row.updated_in_emr_by).trim() !== '' &&
      row?.updated_in_emr_at
  );
}

export function isPatientAwaitingEmrPost(row: {
  posted_to_emr?: boolean;
}): boolean {
  return !row?.posted_to_emr;
}

export function isPatientAwaitingEmrUpdate(row: {
  posted_to_emr?: boolean;
  updated_in_emr?: boolean;
}): boolean {
  return Boolean(row?.posted_to_emr) && row?.updated_in_emr === false;
}

export function canMarkPatientPostedInEmr(row: {
  posted_to_emr?: boolean;
}): boolean {
  return !row?.posted_to_emr;
}

const EDIT_AFTER_POST_MS = 2000;

function isLocalEditAfterEmrPost(
  modifiedAt?: string | Date | null,
  postedAt?: string | Date | null,
  recordStatus?: string | null
): boolean {
  if (recordStatus !== 'updated') {
    return false;
  }
  if (modifiedAt == null || modifiedAt === '' || postedAt == null || postedAt === '') {
    return false;
  }
  const modifiedTime = new Date(modifiedAt).getTime();
  const postedTime = new Date(postedAt).getTime();
  if (Number.isNaN(modifiedTime) || Number.isNaN(postedTime)) {
    return false;
  }
  return modifiedTime - postedTime > EDIT_AFTER_POST_MS;
}

export function canMarkPatientUpdatedInEmr(row: {
  posted_to_emr?: boolean;
  updated_in_emr?: boolean;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  return (
    Boolean(row?.posted_to_emr) &&
    row?.updated_in_emr === false &&
    isLocalEditAfterEmrPost(row?.modified_at, row?.posted_at, row?.record_status)
  );
}

export function showsPatientEmrSyncedChip(row: {
  posted_to_emr?: boolean;
  updated_in_emr?: boolean;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  return Boolean(row?.posted_to_emr) && !canMarkPatientUpdatedInEmr(row);
}

export function showsPatientUpdatedEmrDetails(row: {
  posted_to_emr?: boolean;
  updated_in_emr?: boolean;
  updated_in_emr_by?: string | number | null;
  updated_in_emr_at?: string | null;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  return (
    isPatientEmrUpdateConfirmed(row) &&
    isLocalEditAfterEmrPost(row?.modified_at, row?.posted_at, row?.record_status)
  );
}

export function showsPatientPostedEmrDetails(row: {
  posted_to_emr?: boolean;
  updated_in_emr?: boolean;
  updated_in_emr_by?: string | number | null;
  updated_in_emr_at?: string | null;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  return Boolean(row?.posted_to_emr) && !showsPatientUpdatedEmrDetails(row);
}

export function isPatientEmrSyncPending(row: {
  posted_to_emr?: boolean;
  updated_in_emr?: boolean;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  return !row?.posted_to_emr || canMarkPatientUpdatedInEmr(row);
}
